// HealthBadge — farbige Ampel (grün/gelb/rot/neutral) für die berechnete
// Projekt-Health. Wird im Projekt-Detail-Kopf und auf der ProjectCard genutzt.
// Die Bewertung selbst kommt aus computeProjectHealth (lib/projectHealth).

import type { HealthInput, HealthLevel } from "../lib/projectHealth";
import { computeProjectHealth } from "../lib/projectHealth";

const DOT_COLOR: Record<HealthLevel, string> = {
  green: "var(--color-success, #16A34A)",
  yellow: "var(--color-warning, #CA8A04)",
  red: "var(--color-error)",
  neutral: "var(--color-on-surface-variant)",
};

interface Props {
  project: HealthInput;
  /** "dot" = nur Punkt (kompakt, z. B. Card-Header); "full" = Punkt + Label. */
  variant?: "dot" | "full";
}

export function HealthBadge({ project, variant = "full" }: Props): preact.JSX.Element {
  const health = computeProjectHealth(project);
  const color = DOT_COLOR[health.level];
  const title = `${health.label} — ${health.reason}`;

  if (variant === "dot") {
    return (
      <span
        class="inline-flex items-center shrink-0"
        title={title}
        aria-label={`Projekt-Health: ${title}`}
        data-testid="health-badge"
        data-health={health.level}
      >
        <span
          class="inline-block size-2.5 rounded-full"
          style={{ background: color }}
          aria-hidden="true"
        />
      </span>
    );
  }

  return (
    <span
      class="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-0.5 text-label font-semibold text-[color:var(--color-on-surface)] shrink-0"
      title={title}
      aria-label={`Projekt-Health: ${title}`}
      data-testid="health-badge"
      data-health={health.level}
    >
      <span
        class="inline-block size-2 rounded-full shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      />
      {health.label}
    </span>
  );
}
